import { t } from "../../i18n";
import { useEffect } from "react";
import { create } from "zustand";
import { analyzeWall, fmt, makeDefaultPricing, type WallInput } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { useCadUi } from "../../state/cadUiStore";
import { useAutoSizeStore } from "../../state/useAutoSize";
import { applyCandidate, currentHeight, useAppliedStore } from "./SmartDesignLayout";

/**
 * "Yeniden boyutlandır" (Cem 2026-10-01: "Akıllı tasarım ekranına dönmeden yeniden hesapla"): seçili duvarın güncel
 * girdileriyle (su, kontrol ayarları, zemin…) aynı taramayı çalıştırır ve duvarın kendi tipindeki en ucuz kurtaran kesiti
 * doğrudan uygular. Sonuç Model paftasındaki "uygulandı" bandında (Geri al) ve komut satırında görünür.
 */
type Kind = "masonry-gravity" | "rc-cantilever";
interface Pending { wallId: string; kind: Kind; height: number; length: number }
const usePending = create<{ p: Pending | null; set(p: Pending | null): void }>((set) => ({ p: null, set: (p) => set({ p }) }));

/** Taranabilir duvar tipi: parametrik taş ya da payandasız konsol (çokgen/payandalı kesit tarama ile ezilmesin) */
export function resizeKind(w: WallInput): Kind | null {
  if (w.geometry.mode === "parametric") return "masonry-gravity";
  if (w.geometry.mode === "rc-cantilever" && !w.geometry.counterforts) return "rc-cantilever";
  return null;
}

export function startResize(wall: WallInput, pricing = useProjectStore.getState().project?.pricing ?? makeDefaultPricing()): void {
  const kind = resizeKind(wall);
  if (!kind) return;
  const saved = useAutoSizeStore.getState().inputs[wall.id];
  const height = saved?.height ?? currentHeight(wall), length = saved?.length ?? wall.length ?? 10;
  usePending.getState().set({ wallId: wall.id, kind, height, length });
  useCadUi.getState().pushHistory({ text: `BOYUTLANDIR  ${wall.name}: H = ${fmt(height, 2)} m — ${t("güncel girdilerle yeniden hesaplanıyor…")}`, tone: "echo" });
  useAutoSizeStore.getState().run(wall, { height, pricing });
}

/** Tarama bitince bekleyen "yeniden boyutlandır" isteğini uygular (şeritte bir kez bağlanır) */
export function useResizeWatcher(): void {
  const status = useAutoSizeStore((s) => s.status), report = useAutoSizeStore((s) => s.report), error = useAutoSizeStore((s) => s.error), wallId = useAutoSizeStore((s) => s.wallId);
  useEffect(() => {
    const p = usePending.getState().p;
    if (!p || wallId !== p.wallId || status === "running" || status === "idle") return;
    usePending.getState().set(null);
    const cad = useCadUi.getState(), { project, updateWall } = useProjectStore.getState();
    const wall = project?.walls.find((w) => w.id === p.wallId);
    if (!wall) return;
    const c = report?.candidates.find((x) => x.structureType === p.kind);
    if (status === "error" || !c) {
      const reason = error ?? report?.failures.find((x) => x.structureType === p.kind)?.reason ?? t("Uygun kesit bulunamadı.");
      useAppliedStore.getState().set({ wallId: wall.id, prev: structuredClone(wall), text: reason, failed: true });
      cad.pushHistory({ text: `BOYUTLANDIR  ${wall.name}: ${reason}`, tone: "err" });
      return;
    }
    const before = ((): number | null => { try { return analyzeWall(wall).geometry.B; } catch { return null; } })();
    useAppliedStore.getState().set({ wallId: wall.id, prev: structuredClone(wall), text: `${t(c.label)}: B ${before !== null ? fmt(before, 2) : "—"} → ${fmt(c.B, 2)} m` });
    updateWall(wall.id, (w) => applyCandidate(w, c, p.length));
    useAutoSizeStore.getState().setFresh(null);
    cad.pushHistory({ text: `BOYUTLANDIR  ${c.label}: H = ${fmt(p.height, 2)} m · B = ${fmt(c.B, 2)} m${c.cost !== null ? ` · ${fmt(c.cost, 0)} ${c.currency}/m` : ""} → ${t("duvara uygulandı")}`, tone: "ok" });
    cad.setViewBox(null);
  }, [status, report, error, wallId]);
}

export function useResizeRunning(): boolean {
  const running = useAutoSizeStore((s) => s.status === "running"), pending = usePending((s) => s.p !== null);
  return running && pending;
}
